import { useState, useEffect } from 'react';
import { Plus, X, Trash2 } from 'lucide-react';
import './Admin.css';

function authFetch(url, token, options = {}) {
  return fetch(url, {
    ...options,
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}`, ...options.headers },
  });
}

export default function GroupManagement({ token }) {
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupDetail, setGroupDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState('');
  const [showNewForm, setShowNewForm] = useState(false);
  const [newForm, setNewForm] = useState({ name: '', description: '', color: '#f59e0b' });
  const [newLoading, setNewLoading] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [allPaths, setAllPaths] = useState([]);
  const [addingMember, setAddingMember] = useState(false);
  const [addingPath, setAddingPath] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedPathId, setSelectedPathId] = useState('');

  useEffect(() => {
    loadGroups();
    loadAllUsers();
    loadAllPaths();
  }, [token]);

  async function loadGroups() {
    try {
      const res = await authFetch('/api/admin/groups', token);
      if (res.ok) {
        const data = await res.json();
        setGroups(data.groups || data);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function loadAllUsers() {
    try {
      const res = await authFetch('/api/admin/users', token);
      if (res.ok) {
        const data = await res.json();
        setAllUsers(data.users || data);
      }
    } catch {}
  }

  async function loadAllPaths() {
    try {
      const res = await authFetch('/api/admin/paths', token);
      if (res.ok) {
        const data = await res.json();
        setAllPaths(data.paths || data);
      }
    } catch {}
  }

  async function selectGroup(group) {
    setSelectedGroup(group);
    setDetailLoading(true);
    try {
      const res = await authFetch(`/api/admin/groups/${group.id}`, token);
      if (res.ok) {
        const data = await res.json();
        setGroupDetail(data);
        setEditForm({ name: data.name, description: data.description || '', color: data.color || '#f59e0b' });
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setDetailLoading(false);
    }
  }

  async function handleCreate(e) {
    e.preventDefault();
    setNewLoading(true);
    try {
      const res = await authFetch('/api/admin/groups', token, {
        method: 'POST',
        body: JSON.stringify(newForm),
      });
      if (!res.ok) throw new Error('Failed to create group');
      const created = await res.json();
      setGroups(g => [...g, created.group || created]);
      setShowNewForm(false);
      setNewForm({ name: '', description: '', color: '#f59e0b' });
    } catch (err) {
      setError(err.message);
    } finally {
      setNewLoading(false);
    }
  }

  async function handleUpdate(e) {
    e.preventDefault();
    if (!selectedGroup) return;
    setSaving(true);
    try {
      const res = await authFetch(`/api/admin/groups/${selectedGroup.id}`, token, {
        method: 'PUT',
        body: JSON.stringify(editForm),
      });
      if (!res.ok) throw new Error('Failed to update group');
      setGroups(g => g.map(x => x.id === selectedGroup.id ? { ...x, ...editForm } : x));
      setGroupDetail(d => ({ ...d, ...editForm }));
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteGroup() {
    if (!selectedGroup || !window.confirm(`Delete group "${selectedGroup.name}"?`)) return;
    try {
      await authFetch(`/api/admin/groups/${selectedGroup.id}`, token, { method: 'DELETE' });
      setGroups(g => g.filter(x => x.id !== selectedGroup.id));
      setSelectedGroup(null);
      setGroupDetail(null);
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleAddMember() {
    if (!selectedGroup || !selectedUserId) return;
    try {
      const res = await authFetch(`/api/admin/groups/${selectedGroup.id}/members`, token, {
        method: 'POST',
        body: JSON.stringify({ userId: selectedUserId }),
      });
      if (!res.ok) throw new Error('Failed to add member');
      await selectGroup(selectedGroup);
      setAddingMember(false);
      setSelectedUserId('');
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleRemoveMember(memberId) {
    if (!selectedGroup) return;
    try {
      await authFetch(`/api/admin/groups/${selectedGroup.id}/members/${memberId}`, token, { method: 'DELETE' });
      setGroupDetail(d => ({ ...d, members: d.members.filter(m => m.id !== memberId) }));
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleAssignPath() {
    if (!selectedGroup || !selectedPathId) return;
    try {
      const res = await authFetch(`/api/admin/groups/${selectedGroup.id}/paths`, token, {
        method: 'POST',
        body: JSON.stringify({ pathId: selectedPathId }),
      });
      if (!res.ok) throw new Error('Failed to assign path');
      await selectGroup(selectedGroup);
      setAddingPath(false);
      setSelectedPathId('');
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleRemovePath(pathId) {
    if (!selectedGroup) return;
    try {
      await authFetch(`/api/admin/groups/${selectedGroup.id}/paths/${pathId}`, token, { method: 'DELETE' });
      setGroupDetail(d => ({ ...d, paths: d.paths.filter(p => p.id !== pathId) }));
    } catch (err) {
      setError(err.message);
    }
  }

  if (loading) return <div className="admin-section"><p style={{ color: 'var(--text-tertiary)' }}>Loading...</p></div>;

  return (
    <div className="admin-section">
      <div className="admin-section-header">
        <div>
          <h2 className="admin-section-title">Groups</h2>
          <p className="admin-section-desc">{groups.length} group{groups.length !== 1 ? 's' : ''}</p>
        </div>
        <button className="btn-sm btn-primary" onClick={() => setShowNewForm(v => !v)}>
          <Plus size={14} /> New Group
        </button>
      </div>

      {error && <div className="admin-error">{error}</div>}

      {showNewForm && (
        <form className="admin-inline-form" onSubmit={handleCreate}>
          <div className="admin-form-row">
            <label>Name</label>
            <input value={newForm.name} onChange={e => setNewForm(f => ({ ...f, name: e.target.value }))} placeholder="Group name" required />
          </div>
          <div className="admin-form-row">
            <label>Description</label>
            <input value={newForm.description} onChange={e => setNewForm(f => ({ ...f, description: e.target.value }))} placeholder="Optional" />
          </div>
          <div className="admin-form-row">
            <label>Color</label>
            <input type="color" value={newForm.color} onChange={e => setNewForm(f => ({ ...f, color: e.target.value }))} style={{ width: '60px', padding: '2px', height: '36px' }} />
          </div>
          <div className="admin-form-actions">
            <button type="button" className="btn-sm btn-ghost" onClick={() => setShowNewForm(false)}>Cancel</button>
            <button type="submit" className="btn-sm btn-primary" disabled={newLoading}>
              {newLoading ? 'Creating…' : 'Create'}
            </button>
          </div>
        </form>
      )}

      <div className="split-panel">
        <div className="split-panel-left">
          {groups.length === 0 && <div className="empty-admin"><p>No groups yet.</p></div>}
          {groups.map(group => (
            <div
              key={group.id}
              className={`admin-list-item ${selectedGroup?.id === group.id ? 'active' : ''}`}
              onClick={() => selectGroup(group)}
            >
              <div className="group-color-dot" style={{ background: group.color || 'var(--accent)' }} />
              <div className="admin-list-item-info">
                <div className="admin-list-item-name">{group.name}</div>
                <div className="admin-list-item-meta">
                  <span>{group.memberCount ?? group.member_count ?? 0} members</span>
                  <span>{group.pathCount ?? group.path_count ?? 0} paths</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="split-panel-right">
          {!selectedGroup && <div className="empty-admin"><p>Select a group to view details.</p></div>}
          {selectedGroup && detailLoading && <p style={{ color: 'var(--text-tertiary)' }}>Loading…</p>}
          {selectedGroup && !detailLoading && groupDetail && editForm && (
            <div className="admin-detail">
              <div className="admin-detail-header">
                <h3>{groupDetail.name}</h3>
                <button className="btn-sm btn-danger" onClick={handleDeleteGroup}>
                  <Trash2 size={13} /> Delete
                </button>
              </div>

              <form onSubmit={handleUpdate}>
                <div className="admin-form-row">
                  <label>Name</label>
                  <input value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} />
                </div>
                <div className="admin-form-row">
                  <label>Description</label>
                  <input value={editForm.description} onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))} />
                </div>
                <div className="admin-form-row">
                  <label>Color</label>
                  <input type="color" value={editForm.color} onChange={e => setEditForm(f => ({ ...f, color: e.target.value }))} style={{ width: '60px', padding: '2px', height: '36px' }} />
                </div>
                <button type="submit" className="btn-sm btn-primary" disabled={saving} style={{ marginTop: '8px' }}>
                  {saving ? 'Saving…' : 'Save Changes'}
                </button>
              </form>

              {/* Members */}
              <div className="admin-detail-section">
                <div className="admin-detail-section-header">
                  <span>Members ({groupDetail.members?.length || 0})</span>
                  <button className="btn-sm btn-ghost" onClick={() => setAddingMember(v => !v)}>
                    <Plus size={13} /> Add Member
                  </button>
                </div>
                {addingMember && (
                  <div className="admin-inline-form" style={{ marginBottom: '12px' }}>
                    <div className="admin-form-row">
                      <label>User</label>
                      <select value={selectedUserId} onChange={e => setSelectedUserId(e.target.value)}>
                        <option value="">— choose user —</option>
                        {allUsers.map(u => (
                          <option key={u.id} value={u.id}>{u.username}</option>
                        ))}
                      </select>
                    </div>
                    <div className="admin-form-actions">
                      <button className="btn-sm btn-ghost" onClick={() => setAddingMember(false)}>Cancel</button>
                      <button className="btn-sm btn-primary" onClick={handleAddMember} disabled={!selectedUserId}>Add</button>
                    </div>
                  </div>
                )}
                {(groupDetail.members || []).length === 0 && (
                  <p style={{ color: 'var(--text-tertiary)', fontSize: '13px' }}>No members yet.</p>
                )}
                {(groupDetail.members || []).map(member => (
                  <div key={member.id} className="group-member-row">
                    <div className="group-member-info">
                      <span className="group-member-name">{member.username}</span>
                      <span className="badge">{member.role}</span>
                    </div>
                    <button className="action-btn action-btn-danger" onClick={() => handleRemoveMember(member.id)}>
                      <X size={13} />
                    </button>
                  </div>
                ))}
              </div>

              {/* Assigned Paths */}
              <div className="admin-detail-section">
                <div className="admin-detail-section-header">
                  <span>Assigned Paths ({groupDetail.paths?.length || 0})</span>
                  <button className="btn-sm btn-ghost" onClick={() => setAddingPath(v => !v)}>
                    <Plus size={13} /> Assign Path
                  </button>
                </div>
                {addingPath && (
                  <div className="admin-inline-form" style={{ marginBottom: '12px' }}>
                    <div className="admin-form-row">
                      <label>Path</label>
                      <select value={selectedPathId} onChange={e => setSelectedPathId(e.target.value)}>
                        <option value="">— choose path —</option>
                        {allPaths.map(p => (
                          <option key={p.id} value={p.id}>{p.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="admin-form-actions">
                      <button className="btn-sm btn-ghost" onClick={() => setAddingPath(false)}>Cancel</button>
                      <button className="btn-sm btn-primary" onClick={handleAssignPath} disabled={!selectedPathId}>Assign</button>
                    </div>
                  </div>
                )}
                {(groupDetail.paths || []).length === 0 && (
                  <p style={{ color: 'var(--text-tertiary)', fontSize: '13px' }}>No paths assigned.</p>
                )}
                {(groupDetail.paths || []).map(path => (
                  <div key={path.id} className="group-member-row">
                    <div className="group-member-info">
                      <span>{path.icon || '📚'}</span>
                      <span className="group-member-name">{path.name}</span>
                    </div>
                    <button className="action-btn action-btn-danger" onClick={() => handleRemovePath(path.id)}>
                      <X size={13} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
